import React, { useEffect, useRef, useState } from "react";
import requestFrame from "../../shared/request-frame.js";
import type { GameState, Player } from "../../shared/types";
import Overlay from "./Overlay";
import * as gsf from "core/src/core/game-state-functions.js";
import { wormColors } from "core/src/core/constants.js";
import { keyLabel } from "../../shared/key-label";
import { parseEvent } from "../../shared/key-util.js";
import type { HintPhase } from "../../shared/first-run";
import { toView, useArenaView } from "../arena-view";

// The first-run hints on the board (first-run.ts decides who sees them), keys
// only: each of this device's heads gets its two turn keys as cards either
// side of it, pulsing slowly, from the head's roll-call beat to GO. A card
// lights up while its key is held, so trying the keys answers back. The roll
// call's name tags stay off the board in this mode. (Touch has nothing here:
// the steering zones are the TouchSteeringOverlay's, and the roll call's tag
// reads "This is you", see SpawnTags youTag.) Placed like
// the roll call's tags: in map units over the arena, at the head's spawn
// spot, which stands still until GO.
interface Props {
  gameState: GameState;
  players: Player[];
  youIds: string[];
  // each of your seats' keys, or null on touch
  youKeys: Record<string, { left: string | null; right: string | null } | null>;
  touch: boolean;
  // the current phase (first-run.ts useHintPhase)
  phase: HintPhase;
  // each player's roll-call beat (round-intro.ts): a head's cards start as
  // the head fades in
  delays?: Record<string, number>;
  getRenderTime?: () => number;
}

export default function FirstRunHints({ gameState, players, youIds, youKeys, touch, phase, delays, getRenderTime }: Props) {
  const { rotated } = useArenaView();
  // Which of your heads have had their beat: re-rendered only as each one
  // comes in (a frame loop that stops once all are in).
  const [inIds, setInIds] = useState<string>("");
  useEffect(() => {
    if (touch || !delays || !getRenderTime) return;
    let id = 0;
    const loop = () => {
      const t = getRenderTime();
      const now = youIds.filter((y) => (delays[y] ?? 0) <= t).join("|");
      setInIds((prev) => (prev === now ? prev : now));
      if (now.split("|").filter(Boolean).length < youIds.length) id = requestFrame(loop);
    };
    id = requestFrame(loop);
    return () => window.cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, touch]);
  // Which of your turn keys are held right now (key-util names, "|"-joined):
  // their cards light up. Only the bound keys are tracked, so other typing
  // doesn't re-render. Listens alongside the steering listener, never
  // preventing anything itself.
  const [held, setHeld] = useState<string>("");
  const bound = touch ? "" : youIds.map((y) => (youKeys[y] ? [youKeys[y]!.left, youKeys[y]!.right].filter(Boolean).join("|") : "")).filter(Boolean).join("|");
  useEffect(() => {
    if (!bound || phase !== "keys") {
      setHeld("");
      return;
    }
    const names = bound.split("|");
    const down = new Set<string>();
    const sync = () => {
      const now = names.filter((n) => down.has(n)).join("|");
      setHeld((prev) => (prev === now ? prev : now));
    };
    const onDown = (e: KeyboardEvent) => { const n = parseEvent(e); if (names.indexOf(n) !== -1) { down.add(n); sync(); } };
    const onUp = (e: KeyboardEvent) => { const n = parseEvent(e); if (down.delete(n)) sync(); };
    // a key released while the window is away never sends its keyup
    const onBlur = () => { down.clear(); sync(); };
    document.addEventListener("keydown", onDown);
    document.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("keydown", onDown);
      document.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [bound, phase]);
  // At GO the phase drops to null: keep the callout up a beat more, fading.
  const [fading, setFading] = useState(false);
  const shownRef = useRef<HintPhase>(null);
  useEffect(() => {
    if (phase !== null) {
      shownRef.current = phase;
      setFading(false);
      return;
    }
    if (shownRef.current === null) return;
    setFading(true);
    const id = setTimeout(() => {
      shownRef.current = null;
      setFading(false);
    }, 450);
    return () => clearTimeout(id);
  }, [phase]);
  const current = phase !== null ? phase : fading ? shownRef.current : null;
  const map: any = gameState.map;
  if (!map || current === null) return null;
  // touch: the roll call's tag reads "This is you" (SpawnTags youTag), so
  // nothing else goes on the board
  if (touch) return null;
  if (current !== "keys") return null;

  const seen: Record<string, boolean> = {};
  const marks: React.ReactNode[] = [];
  const inSet = inIds.split("|");
  (gameState as any).worms.forEach((worm: any) => {
    if (seen[worm.playerId] || youIds.indexOf(worm.playerId) === -1) return;
    if (delays && inSet.indexOf(worm.playerId) === -1) return; // not in yet
    const player = players.find((p) => p.id === worm.playerId);
    if (!player) return;
    const segmentId = worm.trackId || worm.playerId + "_" + worm.id;
    const segs: any[] = (gameState as any).wormPathSegments[segmentId] || [];
    const data: any = segs.length ? gsf.getWormPathSegmentDataAtTime(gameState, segmentId, segs[0].startTime) : null;
    if (!data) return;
    seen[worm.playerId] = true;
    const color = wormColors[player.colorId];
    const v = toView(data.position.x / map.width, data.position.y / map.height, rotated);
    const fx = v.x, fy = v.y;
    const keys = youKeys[player.id];
    const heldSet = held ? held.split("|") : [];
    const keyClass = (side: "left" | "right") =>
      `first-run-key first-run-key-${side}` + (keys && keys[side] && heldSet.indexOf(keys[side]!) !== -1 ? " first-run-key-down" : "");
    marks.push(
      <div key={player.id} className={"first-run-mark" + (fy > 0.84 ? " first-run-mark-low" : "")} style={{ left: `${fx * 100}%`, top: `${fy * 100}%`, color }}>
        <span className={keyClass("left")}><kbd>{keyLabel(keys ? keys.left : null)}</kbd></span>
        <span className={keyClass("right")}><kbd>{keyLabel(keys ? keys.right : null)}</kbd></span>
      </div>
    );
  });
  if (!marks.length) return null;
  return (
    <Overlay gameState={gameState} className={"first-run canvas-overlay-above-wall" + (fading ? " first-run-off" : "")}>
      <div className="first-run-layer">{marks}</div>
    </Overlay>
  );
}
